import React, { useState } from "react";
import { Box, styled } from "@mui/material";
import { HSPHeader } from "./HSPHeader";
import { HSPCoverContent } from "./HSPCoverContent";
import { HSPCompletionContent } from "./HSPCompletionContent";
import { ConnectedCoursesScreen } from "./ConnectedCoursesScreen";
import { KeysToSuccessScreen } from "./KeysToSuccessScreen";
import { HIGH_SCHOOL_PRIORITIES_XP } from "../../../constants";
import type { HighSchoolPrioritiesActivityProps } from "../../../types";

const ActivityContainer = styled(Box, {
  label: "HSPActivityContainer",
  shouldForwardProp: (prop) => prop !== "lightMode",
})<{ lightMode?: boolean }>(({ theme, lightMode }) => ({
  position: "fixed",
  margin: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: lightMode ? "#fff3d6" : "#1c252e",
  zIndex: theme.zIndex.modal,
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: "auto",
}));

type Screen = "start" | "courses" | "keys" | "complete";

// Content screens in order; progress counts the current screen as done and
// fills to 100% on the last one, matching the Job Gear activity.
const CONTENT_SCREENS: Screen[] = ["courses", "keys"];

export const HighSchoolPrioritiesActivity = ({
  hat,
  onClose,
  activityCompletion,
  onComplete,
  onXPEarned,
}: HighSchoolPrioritiesActivityProps) => {
  const [currentScreen, setCurrentScreen] = useState<Screen>("start");
  const xpValue = HIGH_SCHOOL_PRIORITIES_XP;

  const courses = hat.msConnectedCourses || [];
  const keys = hat.msKeysToSuccess || [];

  const getProgress = () => {
    if (currentScreen === "start") return 0;
    if (currentScreen === "complete") return 100;
    const idx = CONTENT_SCREENS.indexOf(currentScreen);
    return ((idx + 1) / CONTENT_SCREENS.length) * 100;
  };

  const handleCompleted = () => {
    if (!activityCompletion) {
      onComplete?.();
      onXPEarned?.(xpValue, `highschoolpriorities-${hat.id}`);
    }
  };

  const handleStart = () => {
    setCurrentScreen("courses");
  };

  const handleFinish = () => {
    handleCompleted();
    setCurrentScreen("complete");
  };

  const handleContinue = () => {
    onClose();
  };

  const isLightMode = currentScreen === "keys";

  return (
    <ActivityContainer lightMode={isLightMode}>
      {currentScreen !== "complete" && (
        <HSPHeader
          onClose={onClose}
          hat={hat}
          progress={getProgress()}
          lightMode={isLightMode}
        />
      )}

      {currentScreen === "start" && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 3,
          }}
        >
          <HSPCoverContent
            hat={hat}
            onStart={handleStart}
            activityCompletion={activityCompletion}
            xpValue={xpValue}
          />
        </Box>
      )}

      {currentScreen === "courses" && (
        <ConnectedCoursesScreen
          courses={courses}
          onNext={() => setCurrentScreen("keys")}
        />
      )}

      {currentScreen === "keys" && (
        <KeysToSuccessScreen
          keys={keys}
          onBack={() => setCurrentScreen("courses")}
          onNext={handleFinish}
          isLast
        />
      )}

      {currentScreen === "complete" && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 3,
          }}
        >
          <HSPCompletionContent
            hat={hat}
            onContinue={handleContinue}
            xpValue={xpValue}
          />
        </Box>
      )}
    </ActivityContainer>
  );
};
